import { useParams, useNavigate, Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { cn } from '../utils'
import { formatCurrency, formatNumber } from '../utils'
import { usePlayer } from '../context/PlayerContext'
import { useToast } from '../context/ToastContext'
import { useDiscogsWrite } from '../hooks/useDiscogsWrite'
import { useSimilarArtists, useReleaseNeighbors, useRelease, useAudioTags, useVideoAudio, releasesKeys, useDiscogsMembership } from '../hooks'
import { ArtistLinks } from '../components/common/ArtistLinks'
import { LoadingState } from '../components/common/Spinner'
import { Button } from '../components/common'
import { ReleaseGrid } from '../components/releases/ReleaseGrid'
import { refreshReleaseStats } from '../api/releases'
import { calculateGemScore } from '../config/constants'
import type { ReleaseCompany, ReleaseExtraArtist } from '../types/release'
import styles from './ReleasePage.module.css'

function trackSeconds(duration?: string): number {
  if (!duration) return 0
  const parts = duration.split(':').map(Number)
  if (parts.some(Number.isNaN)) return 0
  return parts.reduce((acc, n) => acc * 60 + n, 0)
}

function formatDuration(totalSec: number): string {
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function getVideoId(url?: string): string | null {
  if (!url) return null
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/)
  return m ? m[1] : null
}

// Audio model emits compound instrument labels ("electricguitar"); split the common ones.
function humanizeLabel(label: string): string {
  return label
    .replace(/(acoustic|electric|bass|double|drum|pipe|classical)(guitar|bass|piano|machine|organ)/g, '$1 $2')
    .replace(/\b\w/g, c => c.toUpperCase())
}

interface Credit {
  role: string
  names: string[]
}

function buildCredits(companies?: ReleaseCompany[], extraartists?: ReleaseExtraArtist[]): Credit[] {
  const byRole = new Map<string, string[]>()
  const push = (role: string | undefined, name: string | undefined) => {
    if (!role || !name) return
    const list = byRole.get(role) ?? []
    if (!list.includes(name)) list.push(name)
    byRole.set(role, list)
  }
  extraartists?.forEach(a => push(a.role?.replace(/\s*\[[^\]]*\]/g, '').trim(), a.anv || a.name))
  companies?.forEach(c => push(c.entity_type_name, c.name))
  return Array.from(byRole.entries()).map(([role, names]) => ({ role, names }))
}

export function ReleasePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { hasVideos, playRelease, startRadio, stopRadio, radioSeedId } = usePlayer()
  const { showToast } = useToast()
  const { isInWantlist } = useDiscogsMembership()
  const { toggleWantlist } = useDiscogsWrite()
  const [neighborLimit, setNeighborLimit] = useState(12)
  const [includeSameArtist, setIncludeSameArtist] = useState(() => {
    try { return localStorage.getItem('releases:includeSameArtist') === 'true' } catch { return false }
  })
  const [activeTrack, setActiveTrack] = useState<number | null>(null)

  const { data: releaseData, isLoading, isError } = useRelease(Number(id))
  const release = releaseData?.release ?? null
  const gemScore = release ? (release.gem_score ?? calculateGemScore(release.elevated_score, release.want)) : null
  const { data: audioTagsData } = useAudioTags(Number(id))
  const { data: videoAudioData } = useVideoAudio(Number(id))
  const { data: similarArtistsData } = useSimilarArtists(Number(id))
  const { data: similarData, isLoading: neighborsLoading } = useReleaseNeighbors(Number(id), neighborLimit, includeSameArtist)

  useEffect(() => {
    if (!id) return
    refreshReleaseStats(Number(id))
      .then(result => {
        if (result.success && result.updated) {
          queryClient.invalidateQueries({ queryKey: releasesKeys.detail(Number(id)) })
        }
      })
      .catch(() => {})
  }, [id])

  const handlePlayTrack = (videoIndex: number) => {
    if (!release) return
    const count = playRelease(release, videoIndex)
    if (count > 0) {
      setActiveTrack(videoIndex)
    } else {
      showToast('Could not play this track', 'warning')
    }
  }

  const handlePlayAll = () => {
    if (!release || !hasVideos(release)) {
      showToast('No playable videos for this release', 'warning')
      return
    }
    const count = playRelease(release)
    if (count > 0) {
      setActiveTrack(0)
      showToast(`Playing ${count} track${count !== 1 ? 's' : ''}`, 'success')
    }
  }

  const handleToggleRadio = () => {
    if (!release) return
    if (radioSeedId === release.id) {
      stopRadio()
      showToast('Radio off', 'success')
      return
    }
    if (!hasVideos(release)) {
      showToast('No playable videos to start radio', 'warning')
      return
    }
    if (startRadio(release) > 0) {
      setActiveTrack(0)
      showToast('Radio on — playing similar records', 'success')
    }
  }

  if (isLoading) {
    return (
      <div className={styles.page}>
        <LoadingState message="Loading release..." />
      </div>
    )
  }

  if (isError || !release) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState}>
          <i className="bi bi-exclamation-circle"></i>
          <span>{isError ? 'Failed to load release' : 'Release not found'}</span>
          <Button variant="secondary" onClick={() => navigate(-1)}>Go back</Button>
        </div>
      </div>
    )
  }

  const inWantlist = isInWantlist(release.id)
  const radioActive = radioSeedId === release.id
  const mainImage = release.images?.[0]?.uri || null
  const labelName = Array.isArray(release.labels) && release.labels.length > 0 ? release.labels[0].name : null
  const catNo = Array.isArray(release.labels) && release.labels.length > 0 ? release.labels[0].catno : null

  const sortedByConfidence = (obj: Record<string, number> | undefined) =>
    obj ? Object.entries(obj).sort((a, b) => b[1] - a[1]).map(([k]) => k) : []

  const displayGenres = release.genres ?? []
  // Keep the Discogs styles visible until audio analysis contributes real tags.
  // Some analysed releases have an audio row but an empty styles payload; using
  // that empty payload used to make the header tags disappear after the query
  // resolved.
  const audioStyles = audioTagsData?.has_audio
    ? sortedByConfidence(audioTagsData.predicted_styles)
    : []
  const displayStyles = audioStyles.length > 0 ? audioStyles : (release.styles ?? [])
  const displayFormats = Array.from(new Set(release.formats?.map((f: any) => f.name).filter(Boolean) ?? []))
  const formatLine = displayFormats.join(' · ')

  const hasVideosResult = hasVideos(release)
  const tracks = release.tracklist && release.tracklist.length > 0 ? release.tracklist : null
  const totalSeconds = tracks ? tracks.reduce((acc, t) => acc + trackSeconds(t.duration), 0) : 0

  const credits = buildCredits(release.companies, release.extraartists)
  const audioByVideoId = videoAudioData?.by_video_id ?? {}

  const lotParts = [labelName, catNo, release.year].filter(Boolean)

  return (
    <div className={styles.page}>
      <div className={styles.scrollArea}>

        {/* ── Top bar ── */}
        <div className={styles.topBar}>
          <button onClick={() => navigate(-1)} className={styles.backButton}>
            <i className="bi bi-arrow-left"></i>
            Back
          </button>
          <div className={styles.catalogMeta}>
            <span>Release №&nbsp;{release.id}</span>
            {catNo && (
              <>
                <span className={styles.metaSep}>/</span>
                <span>{catNo}</span>
              </>
            )}
            <a
              href={`https://www.discogs.com/release/${release.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.discogsLink}
            >
              Discogs <i className="bi bi-box-arrow-up-right"></i>
            </a>
          </div>
        </div>

        {/* ── Header ── */}
        <div className={styles.header}>
          <div className={styles.coverCol}>
            <div className={styles.coverWrap}>
              {mainImage ? (
                <img src={mainImage} alt={release.title} className={styles.cover} />
              ) : (
                <div className={styles.noCover}><i className="bi bi-disc"></i></div>
              )}
            </div>
            {(labelName || formatLine || release.country) && (
              <div className={styles.coverFootnote}>
                <span>{labelName || '—'}</span>
                <span>{[formatLine, release.country].filter(Boolean).join(' · ')}</span>
              </div>
            )}
          </div>

          <div className={styles.titleCol}>
            {lotParts.length > 0 && (
              <div className={styles.lotline}>
                <span className={styles.lotDot} />
                <span>{lotParts.join(' · ')}</span>
              </div>
            )}
            <h1 className={styles.title}>{release.title}</h1>
            <div className={styles.artistLine}>
              <ArtistLinks artists={release.artists} />
              {(formatLine || release.year) && (
                <span className={styles.artistMeta}>
                  {' · '}{[formatLine, release.year].filter(Boolean).join(' · ')}
                </span>
              )}
            </div>

            <div className={styles.pillRow}>
              {displayGenres.map((g: string) => (
                <span key={g} className={styles.pill}>{g}</span>
              ))}
              {displayStyles.map((s: string) => (
                <span key={s} className={styles.pillStyle}>{s}</span>
              ))}
            </div>

            <div className={styles.actions}>
              {hasVideosResult && (
                <button className={styles.btnPrimary} onClick={handlePlayAll}>
                  <i className="bi bi-play-fill"></i> Play release
                </button>
              )}
              {hasVideosResult && (
                <button
                  className={cn(styles.btnGhost, radioActive && styles.btnGhostActive)}
                  onClick={handleToggleRadio}
                  aria-pressed={radioActive}
                  title="Play an endless mix of similar-sounding records"
                >
                  <i className="bi bi-broadcast"></i>
                  {radioActive ? 'Radio on' : 'Start radio'}
                </button>
              )}
              <button
                className={cn(styles.btnGhost, inWantlist && styles.btnGhostActive)}
                onClick={() => toggleWantlist(release)}
                aria-pressed={inWantlist}
              >
                <i className={`bi ${inWantlist ? 'bi-bookmark-fill' : 'bi-bookmark'}`}></i>
                {inWantlist ? 'On wantlist' : 'Add to wantlist'}
              </button>
            </div>
          </div>
        </div>

        {/* ── Body: tracklist + data sidebar ── */}
        <div className={styles.body}>
          <div className={styles.tracklistCol}>
            {tracks ? (
              <>
                <div className={styles.sectionLabel}>
                  <span className={styles.sectionLabelT}>Tracklist</span>
                  <span className={styles.sectionLabelM}>
                    {tracks.length} track{tracks.length !== 1 ? 's' : ''}
                    {totalSeconds > 0 && ` · ${formatDuration(totalSeconds)}`}
                  </span>
                </div>
                {tracks.map((track, i) => {
                  const video = release.videos?.[i]
                  const hasVideo = !!video
                  const isActive = activeTrack === i
                  const ytId = getVideoId(video?.src || video?.uri)
                  const audio = ytId ? audioByVideoId[ytId] : null
                  const hasTags = !!audio && (audio.styles.length > 0 || audio.instruments.length > 0 || audio.moods.length > 0 || audio.bpm != null || audio.key != null)
                  return (
                    <div
                      key={i}
                      className={cn(styles.trackRow, isActive && styles.trackRowActive, hasVideo && styles.trackRowPlayable)}
                      onClick={() => hasVideo && handlePlayTrack(i)}
                    >
                      <span className={styles.trackPos}>{track.position || String(i + 1)}</span>
                      {hasVideo ? (
                        <button
                          className={cn(styles.trackPlay, isActive && styles.trackPlayActive)}
                          onClick={e => { e.stopPropagation(); handlePlayTrack(i) }}
                        >
                          <i className={`bi ${isActive ? 'bi-pause-fill' : 'bi-play-fill'}`}></i>
                        </button>
                      ) : (
                        <span className={styles.trackPlaySpacer} />
                      )}
                      <div className={styles.trackMain}>
                        <span className={styles.trackTitle}>{track.title}</span>
                        {hasTags && (
                          <div className={styles.trackTags}>
                            {audio!.bpm != null && <span className={styles.tagDsp}>{audio!.bpm} BPM</span>}
                            {audio!.key && <span className={styles.tagDsp}>{audio!.key}</span>}
                            {audio!.styles.map(s => <span key={`s-${s}`} className={styles.tagStyle}>{s}</span>)}
                            {audio!.instruments.map(t => <span key={`i-${t}`} className={styles.tagNeutral}>{humanizeLabel(t)}</span>)}
                            {audio!.moods.map(m => <span key={`m-${m}`} className={styles.tagMood}>{m}</span>)}
                          </div>
                        )}
                      </div>
                      {track.duration && <span className={styles.trackDur}>{track.duration}</span>}
                    </div>
                  )
                })}
              </>
            ) : (
              <div className={styles.emptyTracklist}>
                <i className="bi bi-music-note-list"></i>
                <span>No tracklist available</span>
              </div>
            )}
          </div>

          <aside className={styles.sideCol}>
            <div className={styles.sideBlock}>
              <div className={styles.sideHd}>Pressing</div>
              {labelName && <SideRow k="Label" v={labelName} />}
              {catNo && <SideRow k="Cat №" v={catNo} mono />}
              {release.country && <SideRow k="Country" v={release.country} />}
              {(release.released || release.year) && (
                <SideRow k="Released" v={String(release.released || release.year)} />
              )}
              {formatLine && <SideRow k="Format" v={formatLine} />}
            </div>

            <div className={styles.sideBlock}>
              <div className={styles.sideHd}>Market</div>
              {release.median != null && <SideRow k="Median" v={formatCurrency(release.median)} mono />}
              {(release.low != null || release.high != null) && (
                <SideRow
                  k="Range"
                  mono
                  v={`${release.low != null ? formatCurrency(release.low) : '—'} – ${release.high != null ? formatCurrency(release.high) : '—'}`}
                />
              )}
              {(release.want != null || release.have != null) && (
                <SideRow
                  k="Want / Have"
                  mono
                  v={`${release.want != null ? formatNumber(release.want) : '—'} / ${release.have != null ? formatNumber(release.have) : '—'}`}
                />
              )}
              {release.rating_average != null && (
                <SideRow k="Rating" mono v={`${release.rating_average.toFixed(1)} / 5`} />
              )}
              {release.num_for_sale != null && (
                <SideRow k="For sale" v={formatNumber(release.num_for_sale)} />
              )}
              {release.last_sold_days != null && (
                <SideRow k="Last sold" v={`${release.last_sold_days}d ago`} />
              )}
              {gemScore != null && (
                <SideRow k="Gem Score" v={gemScore.toFixed(2)} accent={gemScore > 0.7} />
              )}
              {release.liquidity_score != null && (
                <SideRow k="Liquidity" mono v={release.liquidity_score.toFixed(2)} />
              )}
            </div>
          </aside>
        </div>

        {/* ── Notes + credits ── */}
        {(release.notes || credits.length > 0) && (
          <div className={styles.notes}>
            <div className={styles.notesGrid}>
              <div className={styles.notesLabel}>Notes</div>
              <div className={styles.notesBody}>
                {release.notes
                  ? release.notes.split(/\n{2,}/).map((p, i) => (
                      <p key={i} className={styles.notesP}>{p}</p>
                    ))
                  : <p className={styles.notesEmpty}>No sleeve notes.</p>}
              </div>
              <div className={styles.creditsCol}>
                {credits.length > 0 && (
                  <>
                    <div className={styles.sideHd}>Credits</div>
                    {credits.map(c => (
                      <SideRow key={c.role} k={c.role} v={c.names.join(', ')} />
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Similar releases ── */}
        {similarData && similarData.releases.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionLabel}>
              <span className={styles.sectionLabelT}>If you like this</span>
              <label className={styles.toggleLabel}>
                <span>Same artist</span>
                <span
                  className={cn(styles.toggleSwitch, includeSameArtist && styles.toggleSwitchOn)}
                  onClick={() => setIncludeSameArtist(v => {
                    const next = !v
                    localStorage.setItem('releases:includeSameArtist', String(next))
                    return next
                  })}
                >
                  <span className={styles.toggleThumb} />
                </span>
              </label>
            </div>
            <ReleaseGrid releases={similarData.releases} draggable />
            {similarData.releases.length >= neighborLimit && (
              <div className={styles.loadMoreRow}>
                <Button variant="secondary" onClick={() => setNeighborLimit(p => p + 12)} disabled={neighborsLoading}>
                  {neighborsLoading ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ── Similar artists ── */}
        {similarArtistsData && similarArtistsData.artists.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionLabel}>
              <span className={styles.sectionLabelT}>Similar Artists</span>
            </div>
            <div className={styles.artistRow}>
              {similarArtistsData.artists.map(artist => (
                <Link
                  key={artist.id}
                  to={`/artists?artist_id=${artist.id}&artist_name=${encodeURIComponent(artist.name)}`}
                  className={styles.artistCard}
                >
                  {artist.cover_image ? (
                    <img src={artist.cover_image} alt={artist.name} className={styles.artistAvatar} />
                  ) : (
                    <div className={styles.artistAvatarEmpty}><i className="bi bi-person"></i></div>
                  )}
                  <span className={styles.artistName}>{artist.name}</span>
                  <span className={styles.artistMetaLabel}>{artist.release_count} releases</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className={styles.bottomPad} />
      </div>
    </div>
  )
}

function SideRow({ k, v, mono, accent }: { k: string; v: string; mono?: boolean; accent?: boolean }) {
  return (
    <div className={styles.sideRow}>
      <span className={styles.sideRowK}>{k}</span>
      <span className={cn(styles.sideRowV, mono && styles.sideRowMono, accent && styles.sideRowAccent)}>{v}</span>
    </div>
  )
}
